import React, { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Report } from 'tabler-icons-react';
import dayjs from 'dayjs';
import { Context as RegistrationAdminContext } from '../../../../providers/RegistrationAdminProvider';
import { useRegAssociationAdminPermissions } from '../../../../helpers/adminHelper';
import { EVALUATION_VIEW_TABS } from '../navigation/links';
import AdminView from '../../../../components/common/AdminView';
import { useModalState, usePaginationFilter } from '../../../../helpers/hooks';
import TableView from '../../../../components/common/TableView';
import AppCard from '../../../../components/common/AppCard';
import { triggerNotification } from '../../../../helpers/notificationHelper';
import CreateEvaluationSessionModal from '../../../../components/content/evaluation/CreateEvaluationSessionModal';
import { formatUtcDate } from '../../../../helpers/format';

const VIEW_ACTIONS_ENUM = {
  view: 'VIEW',
  create: 'CREATE',
  delete: 'DELETE'
};

const VIEW_ACTIONS = [
  {
    label: 'View',
    value: VIEW_ACTIONS_ENUM.view
  },
  {
    label: 'Delete',
    value: VIEW_ACTIONS_ENUM.delete,
    isCustomOnly: true
  }
];

const TABLE_COLUMNS = [
  {
    label: 'Name',
    value: 'name',
    sortable: true
  },
  {
    label: 'Division',
    value: 'division',
    sortable: true
  },
  {
    label: 'Evaluators',
    value: 'evaluators',
    sortable: true
  },
  {
    label: 'Players',
    value: 'players',
    sortable: true
  },
  {
    label: 'Overall Score',
    value: 'overall_score',
    sortable: true
  },
  {
    label: 'Created',
    value: 'created',
    sortable: true
  }
];

const EvaluationSessionTableView = () => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const { state, fetchPaginatedRegScoutReportEvaluationSessions } = useContext(
    RegistrationAdminContext
  );
  const {
    regAssociation,
    isAssociationSuperAdmin
  } = useRegAssociationAdminPermissions();
  const {
    pageIndex,
    pageSize,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh
  } = usePaginationFilter(
    {
      search: '',
      status: ''
    },
    (newFilter) => {
      fetchPaginatedRegScoutReportEvaluationSessions(
        regAssociation.pkRegAssociation,
        newFilter,
        null,
        (error) => {
          triggerNotification(error);
        }
      );
    },
    state.paginatedRegScoutReportEvaluationSessions,
    'evaluation_sessions'
  );
  const loading = !hasFetched.current || dataLoading;
  const noDataCreated = !loading && totalNoFilterCount === 0;

  const regAssociationDivisionOptions =
    state.regScoutReportEvaluationOptions.value &&
    state.regScoutReportEvaluationOptions.value?.pkRegAssociation ===
      regAssociation?.pkRegAssociation
      ? state.regScoutReportEvaluationOptions.value.regAssociationDivisions
          .filter((d) => d.hasEvaluationSessions)
          .map((d) => ({
            label: d.label,
            value: d.value.toString()
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];

  useEffect(() => {
    if (regAssociation) {
      onRefresh();
      hasFetched.current = true;
    }
  }, [regAssociation]);

  return (
    <AdminView
      actionControl={{
        label: 'Create Session',
        icon: <Plus />,
        action: 'CREATE'
      }}
      headerTitle="Scouting & Evaluations"
      onAction={onOpenModal}
      tabs={
        isAssociationSuperAdmin
          ? EVALUATION_VIEW_TABS
          : EVALUATION_VIEW_TABS.filter((t) => !t.isSuperAdminOnly).map(
              (t) => ({
                ...t,
                control: null
              })
            )
      }
    >
      <AppCard style={{ flex: 1, padding: 0, height: '100%' }}>
        <TableView
          columns={TABLE_COLUMNS}
          disabled={noDataCreated}
          disabledContent={{
            title: 'No sessions created',
            description: 'Start by creating a session and adding players.',
            icon: <Report color="#000" size={125} />,
            primaryButton: {
              label: 'Create Session',
              icon: <Plus size={18} />,
              onClick: () => onOpenModal(VIEW_ACTIONS_ENUM.create)
            }
          }}
          emptyMessage="No sessions found."
          enableInputsWhenLoading
          filters={[
            {
              key: 1,
              label: 'Division',
              type: 'select',
              value: filter.fkRegAssociationDivision,
              placeholder: 'Filter by division',
              data: regAssociationDivisionOptions,
              clearable: true,
              searchable: true,
              onChange: (v) =>
                onFilterChange({ ...filter, fkRegAssociationDivision: v }, true)
            },
            {
              key: 2,
              label: 'Session Name',
              type: 'text',
              value: filter.search,
              placeholder: 'Filter by session',
              onChange: (value) => onFilterChange({ ...filter, search: value })
            }
          ]}
          isDescendingSort={isDescendingSort}
          isLoading={loading}
          noSort
          onAction={(action, item) => {
            if (action === VIEW_ACTIONS_ENUM.view) {
              navigate(`/admin/evaluations/sessions/${item.key}`);
            }
            else {
              onOpenModal(
                action,
                data.find(
                  (s) => s.pkRegScoutReportEvaluationSession === item.key
                )
              );
            }
          }}
          onChangeSortBy={onSortChange}
          onPageChange={onPageChange}
          onRefresh={onRefresh}
          pageIndex={pageIndex}
          rows={data.map((s) => ({
            key: s.pkRegScoutReportEvaluationSession,
            actions: VIEW_ACTIONS,
            columns: [
              {
                key: 1,
                label: s.name,
                weight: 500,
                icon: <Report color="#000" size="30" />,
                onClick: () =>
                  navigate(
                    `/admin/evaluations/sessions/${s.pkRegScoutReportEvaluationSession}`
                  ),
                showOnMobile: true
              },
              {
                key: 2,
                label: s.regAssociationDivision.name,
                subLabel: s.regAssociationDivision.regAssociationSeason.name,
                showOnMobile: true
              },
              {
                key: 3,
                label: s.evaluatorCount
              },
              {
                key: 4,
                label: s.playerCount
              },
              {
                key: 5,
                label: s.overallScore || ''
              },
              {
                key: 6,
                label: dayjs(formatUtcDate(s.createdAt)).format('MMMM D, YYYY'),
                showOnMobile: true
              }
            ]
          }))}
          sortBy={filter.sort}
          tableTitle="Evaluation Sessions"
          totalCount={totalCount}
          totalPages={totalPages}
        />

        <CreateEvaluationSessionModal
          isOpen={modalState.isOpen}
          onClose={onCloseModal}
          onSuccess={onRefresh}
          pkRegAssociation={regAssociation?.pkRegAssociation}
          regScoutReportEvaluationSession={modalState.item}
          showDelete={modalState.action === VIEW_ACTIONS_ENUM.delete}
        />
      </AppCard>
    </AdminView>
  );
};

export default EvaluationSessionTableView;
